import {
  type FC,
  useEffect,
  useMemo,
} from 'react';
import { useDispatch } from 'react-redux';
import { useRequiresCleanMap } from '~/_shared/utils/hooks/useRequiresCleanMap';
import { notNullOrUndefined } from '~/_shared/utils/typeGuards';
import { useWaitForDataReady } from '~/map/afterMigrationMapPrepareModal/useWaitForDataReady';
import { routeUiDataLoaded } from '~/store/frontendState/mapTools/directions/directions.actionCreators';
import { emptyRouteUiData } from '~/store/frontendState/mapTools/directions/directions.state';
import { selectRoutesWithVisibleMarkers } from '~/store/mapSettings/directions/selectRoutesWithVisibleMarkers';
import { useHiddenRouteIdsSelector } from '~/store/mapSettings/toolsState/visibility/visibility.selectors';
import { useIsUserOverDirectionsApiLimit } from '~/store/userData/userData.selectors';
import { useSelector } from '../../../_shared/utils/hooks/useSelector';
import { useFindDirections } from '../../../directions/useFindDirections.hook';
import { useMapSettingsDirectionsSelectedCollapsingSelector } from '../../../store/mapSettings/directions/mapSettingsDirections.selectors';
import { useMapContext } from '../mapContext';
import { DirectionsRouteInstanceContainer } from './directionsRouteInstance.container';
import { MapDirectionsManager } from './mapDirectionsManager';

export const DirectionsOverlayContainer: FC = () => {
  const dispatch = useDispatch();
  const routes = useSelector(selectRoutesWithVisibleMarkers);
  const routesUiData = useSelector(s => s.frontendState.mapTools.directions.routesUiData);
  const routeCollapsingLevel = useMapSettingsDirectionsSelectedCollapsingSelector();
  const hiddenRouteIds = useHiddenRouteIdsSelector();
  const isMapReady = useWaitForDataReady();
  const overDirectionsApiUsage = useIsUserOverDirectionsApiLimit();
  const cleanMapRequired = useRequiresCleanMap();

  const { map, webglLayers } = useMapContext();
  const manager = useMemo(() => new MapDirectionsManager(map, webglLayers), [map, webglLayers]);

  const { findDirections } = useFindDirections();

  const visibleRoutes = useMemo(() => {
    return routes.filter(r => !hiddenRouteIds.includes(r.id));
  }, [routes, hiddenRouteIds]);

  useEffect(() => {
    const routesToFind = visibleRoutes.filter(r => !routesUiData.has(r.id));

    // route data are dependent on spreadsheet data, so we don't want to fetch before
    // spreadsheet data is loaded, otherwise we would be re-fetching again
    if (!isMapReady || routesToFind.length === 0) {
      return;
    }

    if (overDirectionsApiUsage) {
      return;
    }

    const results = findDirections(routesToFind.map(r => ({
      waypoints: r.waypoints,
      optimize: false,
      shouldReplaceFirstWithUserLocation: false,
    })));
    results.then(directionsResults => {
      const routeData = directionsResults
        .map(({ success }, index) => {
          if (success && routesToFind[index]?.id) {
            return {
              routeId: routesToFind[index].id,
              routeCollapsingLevel,
              data: {
                ...emptyRouteUiData,
                legs: success.legs.map(l => l.data),
                copyrights: success.copyrights,
                apiResponses: success.apiResponses,
              },
            };
          }
          return null;
        })
        .filter(notNullOrUndefined);

      dispatch(routeUiDataLoaded(routeData));
    });
  }, [visibleRoutes, routesUiData, dispatch, findDirections, routeCollapsingLevel, isMapReady, overDirectionsApiUsage]);

  if (cleanMapRequired) {
    return null;
  }

  return (
    <>
      {visibleRoutes.map(r => (
        <DirectionsRouteInstanceContainer
          key={r.id}
          manager={manager}
          route={r}
          routeUiData={routesUiData.get(r.id)}
        />
      ))}
    </>
  );
};
